/** What a table row does, in one fixed-height line: one main action as a small text button and every
 * other action behind a "More" menu - U6, 2026-11-03.
 *
 * A row that stacks two buttons is a row 83px tall (Processing, Missing), so fifty rows are four screens.
 * A row is a fixed 40px: the one thing most people do to it is drawn, the rest are items of the menu.
 * The menu is drawn at the trigger's place on the screen (`PopMenu floating`) so the cell, which clips what
 * overflows it, does not cut it. Draws nothing when there is neither a main action nor an item.
 */

import { DotsThree } from "@phosphor-icons/react";
import { Fragment } from "react";
import type { ReactNode } from "react";

import styles from "./RowMenu.module.css";
import { PopMenu } from "./tablePower/PopMenu";
import menu from "./tablePower/tablePower.module.css";

export interface RowMenuItem {
  label: string;
  /** What choosing the item does. */
  onSelect?: () => void;
  testId?: string;
  /** Draw the item yourself, for one that is a link (it has an address, so it can be opened in a new tab):
   * the item is given the menu item's class and a function that closes the menu, and must be an element with
   * `role="menuitem"`. `label` is then only the key. */
  render?: (api: { className: string; close: () => void }) => ReactNode;
}

export interface RowMenuProps {
  /** The row's name, for the menu's accessible name ("More actions for Gas, main meter"). */
  name: string;
  /** The one action drawn in the row. */
  main?: { label: string; onSelect: () => void; testId?: string; ariaLabel?: string };
  /** Everything else. */
  items?: readonly RowMenuItem[];
  /** Drawn before the buttons, in the same line (a chip, say). */
  lead?: ReactNode;
}

export function RowMenu({ name, main, items = [], lead }: RowMenuProps) {
  if (main === undefined && items.length === 0 && lead === undefined) return null;
  return (
    <span className={styles.row}>
      {lead}
      {main ? (
        <button
          type="button"
          className={styles.main}
          data-testid={main.testId}
          aria-label={main.ariaLabel}
          onClick={main.onSelect}
        >
          {main.label}
        </button>
      ) : null}
      {items.length > 0 ? (
        <PopMenu
          floating
          align="right"
          label={`More actions for ${name}`}
          testId="row-menu"
          triggerClassName={styles.more}
          trigger={<DotsThree size={18} weight="bold" aria-hidden="true" />}
        >
          {(close) =>
            items.map((item) =>
              item.render ? (
                <Fragment key={item.label}>{item.render({ className: menu.item ?? "", close: () => close(false) })}</Fragment>
              ) : (
                <button
                  key={item.label}
                  type="button"
                  role="menuitem"
                  className={menu.item}
                  data-testid={item.testId}
                  onClick={() => {
                    close(false);
                    item.onSelect?.();
                  }}
                >
                  <span className={menu.itemLabel}>{item.label}</span>
                </button>
              ),
            )
          }
        </PopMenu>
      ) : null}
    </span>
  );
}
